import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { MentionablePerson } from '../lib/types';

/** How many to show at once: enough to find somebody, few enough to read. */
const VISIBLE = 6;

/**
 * The word being typed at the caret, if it is an @mention. Two words are allowed, because
 * "@Chris Breedt" is one name — a third would start swallowing the sentence after it.
 */
function mentionAtCaret(value: string, caret: number): { query: string; from: number } | null {
  const before = value.slice(0, caret);
  const match = /(?:^|\s)@([^\s@]*(?:[ \t][^\s@]*)?)$/.exec(before);
  if (!match) return null;

  return { query: match[1], from: caret - match[1].length - 1 };
}

/**
 * A comment box that offers the people who can be named, rather than making somebody
 * remember how a colleague's name is spelt. The list comes from the API, which resolves
 * mentions with the same list — so anybody it offers really does get notified.
 */
export function MentionTextarea({
  value,
  onChange,
  people,
  onSubmit,
  rows = 3,
  placeholder,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  people: MentionablePerson[];
  /** Enter sends, unless the picker is open — then Enter picks. */
  onSubmit?: () => void;
  rows?: number;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const box = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  // Escape closes the list without giving up the @ that has been typed.
  const [dismissed, setDismissed] = useState(false);

  const mention = dismissed ? null : mentionAtCaret(value, caret);

  const matches = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.trim().toLowerCase();

    return people
      .filter((person) => {
        if (!query) return true;
        const name = person.name.toLowerCase();
        // Matches the start of the name or of any part of it, so "bre" finds Chris Breedt.
        return name.startsWith(query) || name.split(/\s+/).some((part) => part.startsWith(query));
      })
      .slice(0, VISIBLE);
  }, [mention, people]);

  const open = matches.length > 0;

  useEffect(() => {
    setActive(0);
  }, [mention?.query]);

  function choose(person: MentionablePerson) {
    if (!mention) return;

    const before = value.slice(0, mention.from);
    const after = value.slice(caret);
    // A trailing space, so the next word is not swallowed into the name.
    const inserted = `@${person.name} `;

    onChange(`${before}${inserted}${after}`);

    // Put the caret after the name, which is where somebody carries on typing.
    const at = mention.from + inserted.length;
    requestAnimationFrame(() => {
      box.current?.focus();
      box.current?.setSelectionRange(at, at);
      setCaret(at);
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((index) => (index + 1) % matches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((index) => (index - 1 + matches.length) % matches.length);
        return;
      }
      // Enter and Tab both take the highlighted name. Enter must NOT post the comment here,
      // or picking a name would send a half-written sentence.
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        choose(matches[active]);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setDismissed(true);
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey && onSubmit) {
      event.preventDefault();
      onSubmit();
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      {open && (
        <ul className="mention-list" role="listbox">
          {matches.map((person, index) => (
            <li key={person.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === active}
                className={index === active ? 'mention-option is-active' : 'mention-option'}
                // Mouse down rather than click: the textarea must not lose focus first.
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(person);
                }}
                onMouseEnter={() => setActive(index)}
              >
                <span className="grow" style={{ minWidth: 0 }}>
                  {person.name}
                </span>
                <span className="dim" style={{ fontSize: 11 }}>
                  {person.role === 'admin'
                    ? 'Lucentra'
                    : person.role === 'client' || person.role === 'client_admin'
                      ? 'Customer'
                      : 'Team'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <textarea
        ref={box}
        rows={rows}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
          setCaret(event.target.selectionStart ?? event.target.value.length);
          setDismissed(false);
        }}
        // Moving the caret with the mouse or arrows can land it in an @word, or out of one.
        onSelect={(event) => setCaret((event.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onKeyDown={onKeyDown}
        onBlur={() => setDismissed(true)}
        onFocus={() => setDismissed(false)}
      />
    </div>
  );
}
